import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CartDrawer from "./cartDrawer";
import { useCart } from "../contexts/cartContext";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Button,
  IconButton,
  InputBase,
  Badge,
  Menu,
  MenuItem,
  Popover,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import { useAuth } from "../contexts/authContext";
import type { ProductVariant } from "../types";
import { capFirst } from "../utils/groupVariants";
import logo from "../logoImages/logo-wordmark.png";

const API_URL = process.env.REACT_APP_API_URL ?? "";

const NAV_LINKS = [
  { label: "New Arrivals", to: "/category/new-arrivals" },
  { label: "Best Sellers", to: "/category/best-sellers" },
  { label: "All Products", to: "/" },
];

const Navbar = () => {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { user, isAdmin, signOut } = useAuth();
  const { cartCount } = useCart();
  const [accountAnchor, setAccountAnchor] = useState<null | HTMLElement>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Search popup
  const [searchAnchor, setSearchAnchor] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [allProducts, setAllProducts] = useState<ProductVariant[]>([]);
  const productsLoaded = useRef(false);

  useEffect(() => {
    if (!searchAnchor || productsLoaded.current) return;
    productsLoaded.current = true;
    fetch(`${API_URL}/api/v1/products`)
      .then((r) => r.json())
      .then(setAllProducts)
      .catch(() => {});
  }, [searchAnchor]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery), 250);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const suggestions = debouncedQuery
    ? (() => {
        const seen = new Set<string>();
        return allProducts
          .filter((p) =>
            p.name.toLowerCase().includes(debouncedQuery.toLowerCase())
          )
          .filter((p) => (seen.has(p.name) ? false : seen.add(p.name) && true))
          .slice(0, 5);
      })()
    : [];

  const closeSearch = () => {
    setSearchAnchor(null);
    setSearchQuery("");
    setDebouncedQuery("");
  };

  const goToResults = (query: string) => {
    closeSearch();
    navigate(`/?q=${encodeURIComponent(query)}`);
    setSearchParams({ q: query });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") closeSearch();
    else if (e.key === "Enter" && searchQuery.trim())
      goToResults(searchQuery.trim());
  };

  const handleSignOut = async () => {
    setAccountAnchor(null);
    await signOut();
    navigate("/");
  };

  return (
    <AppBar position="sticky" elevation={0} sx={{ bgcolor: "black" }}>
      <Toolbar
        sx={{
          maxWidth: "80rem",
          width: "100%",
          mx: "auto",
          px: { xs: 2, sm: 4 },
          justifyContent: "space-between",
          minHeight: { xs: 64 },
        }}
      >
        <Box
          component="a"
          href="/"
          sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}
        >
          <Box
            component="img"
            src={logo}
            alt="Shop with Lemanade"
            sx={{ height: { xs: 32, sm: 38 }, width: "auto" }}
          />
        </Box>

        <Box
          component="nav"
          sx={{ display: { xs: "none", md: "flex" }, gap: 3 }}
        >
          {NAV_LINKS.map((link) => (
            <Button
              key={link.label}
              onClick={() => navigate(link.to)}
              sx={{
                color: "white",
                fontSize: "0.72rem",
                letterSpacing: "0.1em",
                fontWeight: 500,
                minWidth: 0,
                px: 0,
                "&:hover": { color: "#a3a3a3", bgcolor: "transparent" },
              }}
            >
              {link.label}
            </Button>
          ))}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {/* Search icon */}
          <IconButton
            aria-label="Search"
            sx={{ color: "white" }}
            onClick={(e) => setSearchAnchor(e.currentTarget)}
          >
            <SearchIcon sx={{ fontSize: 20 }} />
          </IconButton>

          {/* Search popup */}
          <Popover
            open={Boolean(searchAnchor)}
            anchorEl={searchAnchor}
            onClose={closeSearch}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            slotProps={{
              paper: {
                sx: {
                  mt: 1,
                  width: 360,
                  bgcolor: "#111",
                  border: "1px solid #2a2a2a",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
                  borderRadius: 0,
                },
              },
            }}
          >
            {/* Input row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                px: 2,
                py: 1.25,
                gap: 1,
              }}
            >
              <SearchIcon
                sx={{ fontSize: 18, color: "#525252", flexShrink: 0 }}
              />
              <InputBase
                autoFocus
                placeholder="Search by name..."
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                sx={{
                  color: "white",
                  fontSize: "0.85rem",
                  "& input::placeholder": { color: "#525252", opacity: 1 },
                }}
              />
              {searchQuery && (
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchQuery("");
                    setDebouncedQuery("");
                  }}
                  sx={{
                    color: "#525252",
                    p: 0.25,
                    "&:hover": { color: "white" },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>

            <Divider sx={{ borderColor: "#1a1a1a" }} />

            {/* Results */}
            {!debouncedQuery ? (
              <Box sx={{ px: 2, py: 2 }}>
                <Typography
                  sx={{
                    color: "#525252",
                    fontSize: "0.75rem",
                    letterSpacing: "0.05em",
                  }}
                >
                  Start typing to search…
                </Typography>
              </Box>
            ) : suggestions.length === 0 ? (
              <Box sx={{ px: 2, py: 2 }}>
                <Typography sx={{ color: "#525252", fontSize: "0.75rem" }}>
                  No results for "{debouncedQuery}"
                </Typography>
              </Box>
            ) : (
              <>
                {suggestions.map((p) => (
                  <Box
                    key={p.id}
                    onClick={() => goToResults(p.name)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      px: 2,
                      py: 1.25,
                      cursor: "pointer",
                      "&:hover": { bgcolor: "#1a1a1a" },
                      transition: "background-color 0.15s",
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                        bgcolor: "#1a1a1a",
                        overflow: "hidden",
                      }}
                    >
                      {p.image && (
                        <Box
                          component="img"
                          src={p.image}
                          alt={p.name}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          color: "white",
                          fontSize: "0.78rem",
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {capFirst(p.name)}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        color: "#a3a3a3",
                        fontSize: "0.78rem",
                        flexShrink: 0,
                      }}
                    >
                      {p.salePrice != null ? `$${p.salePrice}` : "—"}
                    </Typography>
                  </Box>
                ))}

                <Divider sx={{ borderColor: "#1a1a1a" }} />

                <Box
                  onClick={() => goToResults(debouncedQuery)}
                  sx={{
                    px: 2,
                    py: 1.25,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    "&:hover": { bgcolor: "#1a1a1a" },
                    transition: "background-color 0.15s",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#a3a3a3",
                      fontSize: "0.72rem",
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                    }}
                  >
                    View all{" "}
                    {
                      new Set(
                        allProducts
                          .filter((p) =>
                            p.name
                              .toLowerCase()
                              .includes(debouncedQuery.toLowerCase())
                          )
                          .map((p) => p.name)
                      ).size
                    }{" "}
                    results
                  </Typography>
                  <Typography sx={{ color: "#525252", fontSize: "0.7rem" }}>
                    →
                  </Typography>
                </Box>
              </>
            )}
          </Popover>

          {/* Account icon */}
          <IconButton
            aria-label="Account"
            sx={{ color: "white" }}
            onClick={(e) =>
              user ? setAccountAnchor(e.currentTarget) : navigate("/login")
            }
          >
            <AccountCircleOutlinedIcon sx={{ fontSize: 20 }} />
          </IconButton>

          <Menu
            anchorEl={accountAnchor}
            open={Boolean(accountAnchor)}
            onClose={() => setAccountAnchor(null)}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: 0,
                  mt: 1.5,
                  minWidth: 200,
                  bgcolor: "black",
                  border: "1px solid #2a2a2a",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
                },
              },
            }}
          >
            {user?.email && (
              <Box
                sx={{
                  px: 2,
                  pt: 1.5,
                  pb: 1,
                  borderBottom: "1px solid #2a2a2a",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.65rem",
                    color: "#525252",
                    textTransform: "uppercase",
                    letterSpacing: "0.12em",
                  }}
                >
                  Signed in as
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "#a3a3a3",
                    mt: 0.25,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {user.email}
                </Typography>
              </Box>
            )}
            <MenuItem
              onClick={() => {
                setAccountAnchor(null);
                navigate("/account");
              }}
              sx={{
                fontSize: "0.72rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "white",
                py: 1.5,
                px: 2,
                "&:hover": { bgcolor: "#1a1a1a" },
              }}
            >
              My Account
            </MenuItem>
            {isAdmin && (
              <MenuItem
                onClick={() => {
                  setAccountAnchor(null);
                  navigate("/admin");
                }}
                sx={{
                  fontSize: "0.72rem",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "white",
                  py: 1.5,
                  px: 2,
                  "&:hover": { bgcolor: "#1a1a1a" },
                }}
              >
                Admin Dashboard
              </MenuItem>
            )}
            <Box sx={{ borderTop: "1px solid #2a2a2a", mt: 0.5 }}>
              <MenuItem
                onClick={handleSignOut}
                sx={{
                  fontSize: "0.72rem",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "#525252",
                  py: 1.5,
                  px: 2,
                  "&:hover": { bgcolor: "#1a1a1a", color: "#a3a3a3" },
                }}
              >
                Sign Out
              </MenuItem>
            </Box>
          </Menu>

          {/* Cart */}
          <IconButton
            aria-label="Cart"
            sx={{ color: "white" }}
            onClick={() => setCartOpen(true)}
          >
            <Badge
              badgeContent={cartCount > 0 ? cartCount : null}
              sx={{
                "& .MuiBadge-badge": {
                  bgcolor: "white",
                  color: "black",
                  fontSize: 10,
                  minWidth: 16,
                  height: 16,
                },
              }}
            >
              <ShoppingBagOutlinedIcon sx={{ fontSize: 20 }} />
            </Badge>
          </IconButton>

          <IconButton
            aria-label="Menu"
            sx={{ color: "white", display: { md: "none" } }}
            onClick={() => setMobileNavOpen(true)}
          >
            <MenuIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Toolbar>

      <Drawer
        anchor="right"
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        slotProps={{
          paper: {
            sx: { width: 240, bgcolor: "black" },
          },
        }}
      >
        <List sx={{ pt: 2 }}>
          {NAV_LINKS.map((link) => (
            <ListItemButton
              key={link.label}
              onClick={() => {
                setMobileNavOpen(false);
                navigate(link.to);
              }}
            >
              <ListItemText
                primary={link.label}
                slotProps={{
                  primary: {
                    sx: {
                      color: "white",
                      fontSize: "0.8rem",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                    },
                  },
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Drawer>

      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </AppBar>
  );
};

export default Navbar;
